import { Form, Formik } from "formik";
import { PMButton } from "../../utils/PMButton";
import { PMField } from "../../utils/PMField";
import PMSwitch from "../../utils/PMSwitch";
import { PMNotice } from "../../utils/notifications";
import { PMDialog } from "../../utils/PMDialog";
import { dispatchWithToast } from "../../Utils";
import { updateHourAsync } from "../../../slices/hour";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { HistoriesModalButton } from "../histories/modal-button";
import {
  useCanApproveHours,
  useCanConfirmAgreedOvertime,
} from "../../../routes/roleOnly";
import {
  confirmAgreedOvertime,
  rejectAgreedOvertime,
} from "../../../api/hour";
import {
  AgreedOvertimeBadge,
  isAgreedOvertimePending,
} from "./AgreedOvertimeBadge";
import { HoursSplitDialog } from "./HoursSplitDialog";

export const ApproveHourDialog = ({ hour, onRefresh, open, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const canApproveHours = useCanApproveHours();
  const canConfirmOt = useCanConfirmAgreedOvertime();

  const [errors] = useState([]);
  const [otBusy, setOtBusy] = useState(false);
  const [otNote, setOtNote] = useState("");
  const [splitOpen, setSplitOpen] = useState(false);

  const dialogHeaderActions = [
    {
      component: (
        <HistoriesModalButton module="hour" module_param={hour?.id} />
      ),
      action: () => onClose(),
      color: "text-gray-500",
    },
  ];

  const otPending = isAgreedOvertimePending(hour);
  const showOtBlock =
    Boolean(hour?.agreed_overtime_requested) ||
    Boolean(hour?.agreed_overtime_confirmed);

  const refreshAndClose = (data) => {
    if (typeof onRefresh === "function") {
      onRefresh(data);
    }
    onClose();
  };

  const handleSplitDone = (data) => {
    setSplitOpen(false);
    if (typeof onRefresh === "function") {
      onRefresh(data);
    }
    onClose();
  };

  const handleConfirmOt = async () => {
    if (!hour?.id || !canConfirmOt) return;
    setOtBusy(true);
    try {
      const data = await confirmAgreedOvertime(hour.id, { note: otNote });
      if (data?.errors || data?.response?.status >= 400) {
        toast.error(t("hours.agreed_overtime.confirm_failed"));
        return;
      }
      toast.success(t("hours.agreed_overtime.confirm_success"));
      refreshAndClose(data?.data || data);
    } catch {
      toast.error(t("hours.agreed_overtime.confirm_failed"));
    } finally {
      setOtBusy(false);
    }
  };

  const handleRejectOt = async () => {
    if (!hour?.id || !canConfirmOt) return;
    setOtBusy(true);
    try {
      const data = await rejectAgreedOvertime(hour.id, { note: otNote });
      if (data?.errors || data?.response?.status >= 400) {
        toast.error(t("hours.agreed_overtime.reject_failed"));
        return;
      }
      toast.success(t("hours.agreed_overtime.reject_success"));
      refreshAndClose(data?.data || data);
    } catch {
      toast.error(t("hours.agreed_overtime.reject_failed"));
    } finally {
      setOtBusy(false);
    }
  };

  return (
    <>
      <PMDialog
        title={t("hours.agreed_overtime.process_dialog_title")}
        open={open && !splitOpen}
        onClose={onClose}
        headerActions={dialogHeaderActions}
      >
        {showOtBlock ? (
          <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-slate-900">
                {t("hours.agreed_overtime.section_title")}
              </span>
              <AgreedOvertimeBadge hour={hour} />
            </div>
            <p className="mb-3 text-sm text-slate-600">
              {t("hours.agreed_overtime.process_help", {
                hours: hour?.agreed_overtime_hours ?? "—",
                time: hour?.time ?? "—",
              })}
            </p>
            {canConfirmOt && otPending ? (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-700">
                  {t("hours.agreed_overtime.note_label")}
                  <textarea
                    value={otNote}
                    onChange={(e) => setOtNote(e.target.value)}
                    rows={2}
                    className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    placeholder={t("hours.agreed_overtime.note_placeholder")}
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={otBusy}
                    onClick={() => void handleConfirmOt()}
                    className="rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {t("hours.agreed_overtime.confirm")}
                  </button>
                  <button
                    type="button"
                    disabled={otBusy}
                    onClick={() => void handleRejectOt()}
                    className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50 disabled:opacity-50"
                  >
                    {t("hours.agreed_overtime.reject")}
                  </button>
                </div>
              </div>
            ) : null}
            {showOtBlock && !canConfirmOt && otPending ? (
              <p className="text-xs text-slate-500">
                {t("hours.agreed_overtime.waiting_for_handler")}
              </p>
            ) : null}
          </div>
        ) : null}

        {canApproveHours ? (
          <Formik
            enableReinitialize={false}
            initialValues={{
              id: hour?.id,
              process: true,
              approved: hour?.approved,
              not_approved: hour?.not_approved,
              processed: hour?.processed,
              process_note: hour?.process_note,
            }}
            validationSchema={Yup.object({
              description: Yup.string(),
            })}
            onSubmit={(values, helpers) => {
              dispatchWithToast(
                dispatch(updateHourAsync(values)),
                (data) => {
                  helpers.setStatus({ success: true });
                  helpers.setSubmitting(false);
                  if (typeof onRefresh === "function") {
                    onRefresh(data);
                  }
                  onClose();
                },
                () => {
                  helpers.setSubmitting(false);
                }
              );
            }}
          >
            {(formik) => (
              <Form>
                {errors &&
                  errors.length > 0 &&
                  errors.map((error, index) => (
                    <PMNotice
                      severity={"error"}
                      key={index}
                      title="Error!"
                      description={error}
                    />
                  ))}

                {hour?.id && (
                  <div className="mb-5">
                    <button
                      type="button"
                      onClick={() => setSplitOpen(true)}
                      className="w-full sm:w-auto inline-flex items-center justify-center min-h-[44px] px-4 rounded-xl text-sm font-semibold text-[#0B3A78] bg-[linear-gradient(160deg,#EAF3FD,#F2F8FE)] border border-[#BFDCF7] hover:brightness-[0.98]"
                    >
                      {t("hours.split.open")}
                    </button>
                    <p className="mt-1.5 text-xs text-slate-500">
                      {t("hours.split.open_hint")}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-2 gap-6">
                  <div className="col-span-1 sm:col-span-1">
                    <PMSwitch
                      label={t("Godkjent")}
                      name="approved"
                      onChange={(e) => {
                        formik.setFieldValue("not_approved", false);
                        formik.handleChange(e);
                      }}
                      {...formik}
                    />
                  </div>

                  <div className="col-span-1 sm:col-span-1">
                    <PMSwitch
                      label={t("Ikke godkjent")}
                      name="not_approved"
                      onChange={(e) => {
                        formik.setFieldValue("approved", false);
                        formik.handleChange(e);
                      }}
                      {...formik}
                    />
                  </div>

                  <div className="col-span-2 sm:col-span-2">
                    <PMField
                      label={t("Notat")}
                      name="process_note"
                      multiline={true}
                      rows={2}
                      {...formik}
                    />
                  </div>

                  <div className="col-span-2 sm:col-span-2 flex justify-between">
                    <PMButton
                      type="button"
                      onClick={() => onClose()}
                      className="bg-slate-400 hover:bg-slate-200 hover-bg- max-w-1/2"
                    >
                      {t("Avbryt")}
                    </PMButton>
                    <PMButton className="ml-10 max-w-1/2" type="submit">
                      {t("Lagre")}
                    </PMButton>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        ) : (
          <div className="space-y-4">
            {hour?.id ? (
              <div>
                <button
                  type="button"
                  onClick={() => setSplitOpen(true)}
                  className="w-full sm:w-auto inline-flex items-center justify-center min-h-[44px] px-4 rounded-xl text-sm font-semibold text-[#0B3A78] bg-[linear-gradient(160deg,#EAF3FD,#F2F8FE)] border border-[#BFDCF7] hover:brightness-[0.98]"
                >
                  {t("hours.split.open")}
                </button>
                <p className="mt-1.5 text-xs text-slate-500">
                  {t("hours.split.open_hint")}
                </p>
              </div>
            ) : null}
            <div className="flex justify-end">
              <PMButton
                type="button"
                onClick={() => onClose()}
                className="bg-slate-400 hover:bg-slate-200 max-w-1/2"
              >
                {t("Lukk")}
              </PMButton>
            </div>
          </div>
        )}
      </PMDialog>

      <HoursSplitDialog
        open={splitOpen}
        hour={hour}
        onClose={() => setSplitOpen(false)}
        onDone={handleSplitDone}
      />
    </>
  );
};